import { API_URL } from '@/config';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Upload, Download, Share2, Check, Image, Type, RefreshCw,
  Globe, MessageCircle, Facebook, Instagram, Twitter, Youtube, X, ArrowLeft, QrCode
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { toast } from 'sonner';
import getImageUrl from '@/utils/imageUrl';
import html2canvas from 'html2canvas';

const API = `${API_URL}/api`;

const PublicFlyerPage = () => {
  const { flyerId } = useParams();
  const [flyer, setFlyer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [placeholderData, setPlaceholderData] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPhone, setUserPhone] = useState('');
  const [baseImageData, setBaseImageData] = useState(null); // Store base64 of base image
  
  const previewRef = useRef(null);
  const fileInputRefs = useRef({});

  const socialIcons = {
    website: Globe,
    whatsapp: MessageCircle,
    facebook: Facebook,
    instagram: Instagram,
    twitter: Twitter,
    youtube: Youtube
  };

  useEffect(() => {
    loadFlyer();
  }, [flyerId]);

  // Convert image URL to base64 - tries multiple approaches
  const convertImageToBase64 = async (imageUrl, originalPath) => {
    console.log('convertImageToBase64 called with:', { imageUrl, originalPath });
    
    // Approach 1: Try server-side base64 conversion (most reliable)
    // This works for paths like '/uploads/filename.png'
    if (originalPath) {
      try {
        // Handle both '/uploads/filename' and just 'filename'
        const filename = originalPath.startsWith('/uploads/') 
          ? originalPath.replace('/uploads/', '') 
          : originalPath.split('/').pop();
        
        console.log('Trying server-side base64 for filename:', filename);
        const response = await axios.get(`${API}/files/base64/${filename}`);
        if (response.data?.data_url) {
          console.log('Server-side base64 conversion successful');
          return response.data.data_url;
        }
      } catch (e) {
        console.log('Server-side base64 failed:', e.message);
      }
    }

    // Approach 2: Try fetch API (works when CORS is properly configured)
    try {
      console.log('Trying fetch approach for:', imageUrl);
      const response = await fetch(imageUrl, { mode: 'cors' });
      if (response.ok) {
        const blob = await response.blob();
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            console.log('Fetch approach successful');
            resolve(reader.result);
          };
          reader.onerror = () => {
            console.log('FileReader failed');
            resolve(null);
          };
          reader.readAsDataURL(blob);
        });
      }
    } catch (e) {
      console.log('Fetch approach failed:', e.message);
    }

    // Approach 3: Try canvas with crossOrigin
    console.log('Trying canvas approach');
    return new Promise((resolve) => {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const dataURL = canvas.toDataURL('image/png');
          console.log('Canvas approach successful');
          resolve(dataURL);
        } catch (error) {
          console.error('Canvas error:', error);
          resolve(null);
        }
      };
      
      img.onerror = (e) => {
        console.log('CrossOrigin image load failed:', e);
        resolve(null);
      };
      
      // Add timestamp to bypass cache
      const separator = imageUrl.includes('?') ? '&' : '?';
      img.src = `${imageUrl}${separator}t=${Date.now()}`;
    });
  };

  const loadFlyer = async () => {
    try {
      const response = await axios.get(`${API}/flyers/public/${flyerId}`);
      setFlyer(response.data);
      
      // Initialize placeholder data
      const initialData = {};
      response.data.placeholders?.forEach(ph => {
        initialData[ph.id] = ph.type === 'text' ? '' : null;
      });
      setPlaceholderData(initialData);

      // Pre-load and convert base image to base64 for download functionality
      if (response.data.base_image) {
        const originalPath = response.data.base_image;
        const imageUrl = getImageUrl(originalPath);
        console.log('Loading base image:', imageUrl, 'Original path:', originalPath);
        const base64 = await convertImageToBase64(imageUrl, originalPath);
        if (base64) {
          console.log('Base image converted to base64 successfully');
          setBaseImageData(base64);
        } else {
          console.warn('Failed to convert base image to base64, will use URL directly');
        }
      }
    } catch (err) {
      console.error('Failed to load flyer:', err);
      setError('Flyer not found or inactive');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (placeholderId, file) => {
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setPlaceholderData(prev => ({
        ...prev,
        [placeholderId]: e.target.result
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleTextChange = (placeholderId, value) => {
    setPlaceholderData(prev => ({
      ...prev,
      [placeholderId]: value
    }));
  };

  const validateInputs = () => {
    const requiredPlaceholders = flyer.placeholders?.filter(ph => ph.required) || [];
    for (const ph of requiredPlaceholders) {
      if (!placeholderData[ph.id]) {
        toast.error(`Please fill in: ${ph.label}`);
        return false;
      }
    }
    return true;
  };

  const generatePreview = () => {
    if (!validateInputs()) return;
    setShowPreview(true);
  };

  const generateFinalImage = useCallback(async () => {
    if (!previewRef.current) return;
    
    setGenerating(true);
    try {
      // Wait for all images in the preview container to fully load
      const images = previewRef.current.querySelectorAll('img');
      console.log(`Found ${images.length} images to wait for`);
      
      await Promise.all(
        Array.from(images).map((img) => {
          return new Promise((resolve) => {
            if (img.complete && img.naturalHeight > 0) {
              console.log('Image already loaded:', img.src?.substring(0, 50));
              resolve();
            } else {
              img.onload = () => {
                console.log('Image loaded:', img.src?.substring(0, 50));
                resolve();
              };
              img.onerror = () => {
                console.warn('Image failed to load:', img.src?.substring(0, 50));
                resolve(); // Resolve anyway to not block
              };
            }
          });
        })
      );
      
      // Extra delay to ensure rendering is complete
      await new Promise(resolve => setTimeout(resolve, 300));
      
      console.log('All images loaded, generating canvas...');
      
      const canvas = await html2canvas(previewRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: flyer.background_color || '#FFFFFF',
        logging: true, // Enable logging to debug
        imageTimeout: 30000,
        onclone: (clonedDoc) => {
          // Ensure all images in cloned document use absolute URLs or base64
          const clonedImages = clonedDoc.querySelectorAll('img');
          clonedImages.forEach(img => {
            console.log('Cloned image src:', img.src?.substring(0, 100));
          });
        }
      });
      
      const imageData = canvas.toDataURL('image/png');
      console.log('Canvas generated, data URL length:', imageData.length);
      setGeneratedImage(imageData);
      
      // Save submission
      try {
        await axios.post(`${API}/flyers/public/${flyerId}/generate`, {
          user_name: userName,
          user_email: userEmail,
          user_phone: userPhone,
          placeholder_data: placeholderData,
          generated_image: imageData.substring(0, 100) + '...' // Just store a reference
        });
      } catch (err) {
        console.error('Failed to save submission:', err);
      }
      
      toast.success('Flyer generated successfully!');
    } catch (err) {
      console.error('Failed to generate image:', err);
      toast.error('Failed to generate flyer');
    } finally {
      setGenerating(false);
    }
  }, [flyer, placeholderData, userName, userEmail, userPhone, flyerId]);

  const downloadImage = () => {
    if (!generatedImage) return;
    
    const link = document.createElement('a');
    link.download = `${flyer.name || 'flyer'}.png`;
    link.href = generatedImage;
    link.click();
    toast.success('Flyer downloaded!');
  };

  const shareToWhatsApp = () => {
    if (!generatedImage) {
      toast.error('Please generate the flyer first');
      return;
    }
    // For WhatsApp sharing, we'd need to upload the image first
    // For now, share the flyer link
    const text = `Check out my personalized flyer from ${flyer.name}!`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, '_blank');
  };

  const shareToFacebook = () => {
    const url = window.location.href;
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(fbUrl, '_blank');
  };

  const shareToTwitter = () => {
    const text = `Check out my personalized flyer from ${flyer.name}!`;
    const url = window.location.href;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(twitterUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <X className="w-12 h-12 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">Oops!</h2>
            <p className="text-muted-foreground">{error}</p>
            <Button variant="outline" className="mt-4" onClick={() => window.history.back()}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">{flyer.name}</h1>
          {flyer.description && (
            <p className="text-gray-400">{flyer.description}</p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <Card className="h-fit">
            <CardContent className="pt-6 space-y-6">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Upload className="w-5 h-5 text-amber-500" />
                Design Your Flyer
              </h2>

              {/* User Info (Optional) */}
              <div className="space-y-3 pb-4 border-b">
                <div>
                  <Label className="text-sm text-muted-foreground">Your Name (Optional)</Label>
                  <Input
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    placeholder="Enter your name"
                  />
                </div>
              </div>

              {/* Placeholders */}
              <div className="space-y-4">
                {flyer.placeholders?.map((ph) => (
                  <div key={ph.id} className="space-y-2">
                    <Label className="flex items-center gap-2">
                      {ph.type === 'image' ? (
                        <Image className="w-4 h-4 text-blue-500" />
                      ) : (
                        <Type className="w-4 h-4 text-green-500" />
                      )}
                      {ph.label}
                      {ph.required && <span className="text-red-500">*</span>}
                    </Label>

                    {ph.type === 'image' ? (
                      <div>
                        <input
                          type="file"
                          ref={(el) => fileInputRefs.current[ph.id] = el}
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(ph.id, e.target.files?.[0])}
                        />
                        {placeholderData[ph.id] ? (
                          <div className="relative">
                            <img
                              src={placeholderData[ph.id]}
                              alt={ph.label}
                              className="w-full h-32 object-cover rounded-lg"
                            />
                            <Button
                              variant="secondary"
                              size="sm"
                              className="absolute bottom-2 right-2"
                              onClick={() => fileInputRefs.current[ph.id]?.click()}
                            >
                              <RefreshCw className="w-3 h-3 mr-1" />
                              Change
                            </Button>
                          </div>
                        ) : (
                          <div
                            className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-amber-500 transition-colors"
                            onClick={() => fileInputRefs.current[ph.id]?.click()}
                          >
                            <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                            <p className="text-sm text-muted-foreground">Click to upload {ph.label}</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <Input
                        value={placeholderData[ph.id] || ''}
                        onChange={(e) => handleTextChange(ph.id, e.target.value)}
                        placeholder={ph.placeholder_text || `Enter ${ph.label}`}
                      />
                    )}
                  </div>
                ))}
              </div>

              <Button
                className="w-full"
                style={{ backgroundColor: '#D4AF37' }}
                onClick={generatePreview}
              >
                <Check className="w-4 h-4 mr-2" />
                Preview Flyer
              </Button>
            </CardContent>
          </Card>

          {/* Preview */}
          <div className="space-y-4">
            <Card>
              <CardContent className="pt-6">
                <h2 className="text-lg font-semibold mb-4">Live Preview</h2>
                <div
                  className="relative rounded-lg overflow-hidden mx-auto border"
                  style={{
                    aspectRatio: `${flyer.canvas_width} / ${flyer.canvas_height}`,
                    backgroundColor: flyer.background_color || '#FFFFFF',
                    maxHeight: '500px'
                  }}
                >
                  {(baseImageData || flyer.base_image) && (
                    <img
                      src={baseImageData || getImageUrl(flyer.base_image)}
                      alt="Flyer"
                      className="w-full h-full object-contain"
                    />
                  )}

                  {/* Render placeholders */}
                  {flyer.placeholders?.map((ph) => (
                    <div
                      key={ph.id}
                      className="absolute flex items-center justify-center overflow-hidden"
                      style={{
                        left: `${ph.x - ph.width / 2}%`,
                        top: `${ph.y - ph.height / 2}%`,
                        width: `${ph.width}%`,
                        height: `${ph.height}%`,
                        borderRadius: ph.type === 'image' ? `${ph.borderRadius}%` : `${ph.borderRadius}px`,
                        backgroundColor: !placeholderData[ph.id] ? 'rgba(0,0,0,0.1)' : 'transparent'
                      }}
                    >
                      {ph.type === 'image' ? (
                        placeholderData[ph.id] ? (
                          <img
                            src={placeholderData[ph.id]}
                            alt={ph.label}
                            className="w-full h-full object-cover"
                            style={{
                              borderRadius: `${ph.borderRadius}%`
                            }}
                          />
                        ) : (
                          <div className="text-center text-gray-400 text-xs">
                            <Image className="w-6 h-6 mx-auto mb-1" />
                            {ph.label}
                          </div>
                        )
                      ) : (
                        <div
                          className="w-full h-full flex items-center justify-center p-1"
                          style={{
                            fontSize: `${ph.fontSize * 0.4}px`,
                            color: ph.fontColor || '#000000',
                            fontFamily: ph.fontFamily || 'Arial',
                            textAlign: ph.textAlign || 'center'
                          }}
                        >
                          {placeholderData[ph.id] || (
                            <span className="text-gray-400 text-xs">{ph.label}</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Social icons on flyer */}
                  {flyer.show_social_on_flyer && (
                    <div
                      className="absolute flex gap-1"
                      style={{
                        left: `${flyer.social_position?.x || 50}%`,
                        top: `${flyer.social_position?.y || 92}%`,
                        transform: 'translate(-50%, -50%)'
                      }}
                    >
                      {Object.entries(flyer.social_links || {}).map(([key, value]) => {
                        if (!value) return null;
                        const Icon = socialIcons[key];
                        return (
                          <a
                            key={key}
                            href={value}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-5 h-5 rounded-full bg-gray-800 flex items-center justify-center hover:bg-gray-700 transition-colors"
                          >
                            <Icon className="w-3 h-3 text-white" />
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Social Buttons */}
            {flyer.show_social_buttons && (
              <Card>
                <CardContent className="pt-4">
                  <p className="text-sm text-muted-foreground mb-3">Connect with us:</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(flyer.social_links || {}).map(([key, value]) => {
                      if (!value) return null;
                      const Icon = socialIcons[key];
                      return (
                        <a
                          key={key}
                          href={value}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button variant="outline" size="sm" className="gap-2">
                            <Icon className="w-4 h-4" />
                            {key.charAt(0).toUpperCase() + key.slice(1)}
                          </Button>
                        </a>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Share QR Code */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg border shadow-sm">
                      <QRCodeSVG 
                        value={window.location.href} 
                        size={56}
                        level="M"
                        includeMargin={false}
                      />
                    </div>
                    <div>
                      <p className="font-medium text-sm flex items-center gap-1">
                        <QrCode className="w-4 h-4" />
                        Share this page
                      </p>
                      <p className="text-xs text-muted-foreground">Scan to open on another device</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      toast.success('Link copied!');
                    }}
                  >
                    Copy Link
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Your Personalized Flyer</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {/* Generated Flyer Preview */}
            <div
              ref={previewRef}
              className="relative mx-auto rounded-lg overflow-hidden"
              style={{
                aspectRatio: `${flyer.canvas_width} / ${flyer.canvas_height}`,
                backgroundColor: flyer.background_color || '#FFFFFF',
                maxWidth: '600px'
              }}
            >
              {(baseImageData || flyer.base_image) && (
                <img
                  src={baseImageData || getImageUrl(flyer.base_image)}
                  alt="Flyer"
                  className="w-full h-full object-contain"
                  crossOrigin="anonymous"
                />
              )}

              {/* Render filled placeholders */}
              {flyer.placeholders?.map((ph) => (
                <div
                  key={ph.id}
                  className="absolute flex items-center justify-center overflow-hidden"
                  style={{
                    left: `${ph.x - ph.width / 2}%`,
                    top: `${ph.y - ph.height / 2}%`,
                    width: `${ph.width}%`,
                    height: `${ph.height}%`,
                    borderRadius: ph.type === 'image' ? `${ph.borderRadius}%` : `${ph.borderRadius}px`
                  }}
                >
                  {ph.type === 'image' && placeholderData[ph.id] && (
                    <img
                      src={placeholderData[ph.id]}
                      alt={ph.label}
                      className="w-full h-full object-cover"
                      style={{
                        borderRadius: `${ph.borderRadius}%`
                      }}
                    />
                  )}
                  {ph.type === 'text' && placeholderData[ph.id] && (
                    <div
                      className="w-full h-full flex items-center justify-center p-2"
                      style={{
                        fontSize: `${ph.fontSize}px`,
                        color: ph.fontColor || '#000000',
                        fontFamily: ph.fontFamily || 'Arial',
                        textAlign: ph.textAlign || 'center'
                      }}
                    >
                      {placeholderData[ph.id]}
                    </div>
                  )}
                </div>
              ))}

              {/* Social icons */}
              {flyer.show_social_on_flyer && (
                <div
                  className="absolute flex gap-2"
                  style={{
                    left: `${flyer.social_position?.x || 50}%`,
                    top: `${flyer.social_position?.y || 92}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  {Object.entries(flyer.social_links || {}).map(([key, value]) => {
                    if (!value) return null;
                    const Icon = socialIcons[key];
                    return (
                      <div key={key} className="w-6 h-6 rounded-full bg-gray-800 flex items-center justify-center">
                        <Icon className="w-3 h-3 text-white" />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Generated Image Display */}
            {generatedImage && (
              <div className="mt-4 p-4 bg-green-50 rounded-lg text-center">
                <Check className="w-8 h-8 mx-auto text-green-500 mb-2" />
                <p className="text-green-700 font-medium">Flyer generated successfully!</p>
              </div>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {!generatedImage ? (
              <Button
                onClick={generateFinalImage}
                disabled={generating}
                className="w-full sm:w-auto"
                style={{ backgroundColor: '#D4AF37' }}
              >
                {generating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Generate Flyer
                  </>
                )}
              </Button>
            ) : (
              <>
                <Button onClick={downloadImage} className="w-full sm:w-auto">
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                <Button variant="outline" onClick={shareToWhatsApp} className="w-full sm:w-auto">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
                <Button variant="outline" onClick={shareToFacebook} className="w-full sm:w-auto">
                  <Facebook className="w-4 h-4 mr-2" />
                  Facebook
                </Button>
                <Button variant="outline" onClick={shareToTwitter} className="w-full sm:w-auto">
                  <Twitter className="w-4 h-4 mr-2" />
                  Twitter
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicFlyerPage;
